import { useMemo, useState } from "react";
import { motion } from "framer-motion";

// Traditional benchmarks per product slug — used by both the slider (dollars) and
// the timeline cheatsheet on the /products/website page (days).
const TRAD_COST = {
  "the-founder": { min: 100000, max: 500000, forge_cents: 25000000, unit: "one week of the Founder" },
  "full-albums": { min: 30000, max: 250000, forge_cents: 1799900, unit: "full-album production" },
  "story-treatment": { min: 2000, max: 8000, forge_cents: 99900, unit: "treatment package" },
  "screenplay": { min: 15000, max: 70000, forge_cents: 299900, unit: "feature spec" },
  "visual-development": { min: 60000, max: 250000, forge_cents: 999900, unit: "vis-dev package" },
  "animated-feature": { min: 8000000, max: 30000000, forge_cents: 4999900, unit: "animated feature" },
  "website": { min: 12000, max: 45000, forge_cents: 250000, unit: "studio-grade site" },
  "ad-campaign": { min: 10000, max: 50000, forge_cents: 299900, unit: "quarter-length campaign" },
  "marketing-package": { min: 30000, max: 150000, forge_cents: 499900, unit: "marketing package" },
  "books": { min: 3000, max: 100000, forge_cents: 149900, unit: "manuscript-to-shelf package" },
  "ecourses": { min: 5000, max: 50000, forge_cents: 299900, unit: "full eCourse" },
  "pitch-deck": { min: 2000, max: 25000, forge_cents: 149900, unit: "investor-ready deck" },
  "score-voice": { min: 90000, max: 225000, forge_cents: 999900, unit: "feature score / voice cast" },
};

const TRAD_DAYS = {
  "the-founder": { min: 30, max: 90, forge: 7 },
  "full-albums": { min: 180, max: 540, forge: 42 },
  "story-treatment": { min: 28, max: 56, forge: 7 },
  "screenplay": { min: 60, max: 180, forge: 18 },
  "visual-development": { min: 90, max: 180, forge: 20 },
  "animated-feature": { min: 540, max: 1800, forge: 120 },
  "website": { min: 42, max: 84, forge: 7 },
  "ad-campaign": { min: 28, max: 56, forge: 10 },
  "marketing-package": { min: 60, max: 180, forge: 14 },
  "books": { min: 42, max: 365, forge: 14 },
  "ecourses": { min: 90, max: 180, forge: 28 },
  "pitch-deck": { min: 21, max: 42, forge: 7 },
  "score-voice": { min: 90, max: 365, forge: 21 },
};

function fmtMoney(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${n}`;
}

export default function TraditionalCostSlider({ slug }) {
  const bench = TRAD_COST[slug];
  const days = TRAD_DAYS[slug];
  const [pos, setPos] = useState(50);

  const tradPrice = useMemo(() => {
    if (!bench) return 0;
    return Math.round(bench.min + ((bench.max - bench.min) * pos) / 100);
  }, [bench, pos]);

  if (!bench || !days) return null;

  const forgePrice = bench.forge_cents / 100;
  const forgeCheaper = tradPrice > forgePrice;
  const multiple = forgeCheaper
    ? Math.max(1, Math.round(tradPrice / forgePrice))
    : Math.max(1, Math.round(forgePrice / tradPrice));
  const dayMultiple = Math.max(1, Math.round(days.max / days.forge));

  return (
    <section
      data-testid="traditional-cost-slider"
      className="max-w-[1100px] mx-auto px-6 sm:px-10 pb-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="border border-[#ff5a00]/25 bg-gradient-to-br from-[#0b0b0d] to-[#141010] px-6 sm:px-10 py-8"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00]">
          Drag the market · watch the gap
        </p>
        <p className="font-display italic text-lg sm:text-2xl text-[#ededed] mt-3">
          Slide anywhere in the traditional market and compare.
        </p>

        {/* Comparison bars */}
        <div className="mt-8 grid gap-4">
          <Bar
            label="Traditional market"
            testid="slider-traditional-value"
            color="#3f3f46"
            fill={`${pos}%`}
            valueText={fmtMoney(tradPrice)}
            subText={`typical ${bench.unit} · ${days.min}–${days.max} days`}
          />
          <Bar
            label="Resilience · The Forge"
            testid="slider-forge-value"
            color="#ff5a00"
            fill={`${Math.min(100, (forgePrice / bench.max) * 100)}%`}
            valueText={fmtMoney(forgePrice)}
            subText={`same ${bench.unit} · ${days.forge} days`}
          />
        </div>

        {/* Slider */}
        <div className="mt-8">
          <input
            data-testid="slider-input"
            type="range"
            min={0}
            max={100}
            value={pos}
            onChange={(e) => setPos(Number(e.target.value))}
            className="w-full accent-[#ff5a00]"
          />
          <div className="flex justify-between mt-2 font-mono text-[9px] uppercase tracking-[0.28em] text-[#52525b]">
            <span>{fmtMoney(bench.min)}</span>
            <span>{fmtMoney(bench.max)}</span>
          </div>
        </div>

        {/* Verdict — branches on whether the traditional benchmark is above
            or below the forge price. Premium products (The Founder) sit above
            most of the market on the low end; boutique products sit far below
            it. Both true statements read cleanly. */}
        <p
          data-testid="slider-verdict"
          className="font-display italic text-xl sm:text-2xl text-[#ededed] mt-8 leading-snug"
        >
          {forgeCheaper ? (
            <>
              At this price point, the forge is <span className="text-[#ff5a00] not-italic font-display">{multiple}×</span> cheaper
              and <span className="text-[#ff5a00] not-italic font-display">{dayMultiple}×</span> faster.
            </>
          ) : (
            <>
              At this price point, the market runs <span className="text-[#ff5a00] not-italic font-display">{multiple}×</span> under the forge —
              and the forge still ships <span className="text-[#ff5a00] not-italic font-display">{dayMultiple}×</span> faster. Different tier. Different receipts.
            </>
          )}
        </p>
      </motion.div>
    </section>
  );
}

function Bar({ label, testid, color, fill, valueText, subText }) {
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#a1a1aa]">{label}</p>
        <p data-testid={testid} className="font-display text-2xl sm:text-3xl leading-none" style={{ color }}>
          {valueText}
        </p>
      </div>
      <div className="relative h-2 mt-2 bg-[#141417] overflow-hidden">
        <motion.div
          initial={false}
          animate={{ width: fill }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="absolute inset-y-0 left-0"
          style={{ backgroundColor: color }}
        />
      </div>
      <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#52525b] mt-1">{subText}</p>
    </div>
  );
}
